import { useEffect, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import analyticsService from '../services/analyticsService';

/**
 * Custom hook for analytics tracking
 * Provides easy access to analytics functions throughout the app
 */
export const useAnalytics = () => {
  const location = useLocation();
  const { user } = useContext(AuthContext);

  // Initialize analytics early (even for anonymous users) to capture UTM params on initial load
  useEffect(() => {
    // Initialize immediately on mount to ensure UTM params are captured on first page load
    // This runs before user context is available, so we start with anonymous tracking
    analyticsService.init(null, null);
  }, []);

  // Update analytics when user changes
  useEffect(() => {
    if (user?.id) {
      analyticsService.init(user.id, user.token);
      analyticsService.setUserProperties({
        user_id: user.id,
        subscription_status: user.subscriptionStatus || 'trial',
        account_type: user.accountType || 'standard'
      });
    } else {
      // Update to anonymous if user logs out
      analyticsService.init(null, null);
    }
  }, [user?.id, user?.token]);

  // Track page views on route changes and when auth hydrates (dashboard routes
  // need userId; the first pass can land before AuthContext restores the session).
  useEffect(() => {
    analyticsService.trackPageView(location.pathname, null, location.search);
  }, [location.pathname, location.search, user?.id]);

  // Wrapper function for tracking events
  const trackTradingEvent = useCallback((eventType, tradeData) => {
    analyticsService.trackTradingEvent(eventType, tradeData);
  }, []);

  return {
    trackTradingEvent,
    // Direct access to service for advanced use cases
    analyticsService
  };
};

export default useAnalytics;
